import { Component, inject } from '@angular/core';
import { BasePageComponent } from '../../../_shared/components/page/base-page.component';
import { AccountFilterComponent } from '../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../_shared/models';
import { DateInterval } from '../../../_shared/enums';
import { CommonModule } from '@angular/common';
import { OverdueMaintenanceComponent } from './components/widgets/overdue-maintenance/overdue-maintenance.component';
import { UpcomingMaintenanceComponent } from './components/widgets/upcoming-maintenance/upcoming-maintenance.component';
import { UnplannedRepairsComponent } from './components/widgets/unplanned-repairs/unplanned-repairs.component';
import { MaintenanceCostYtdComponent } from './components/widgets/maintenance-cost-ytd/maintenance-cost-ytd.component';
import { PreventiveVsReactiveComponent } from './components/graphs/preventive-vs-reactive/preventive-vs-reactive.component';
import { AssetsInMaintenanceComponent } from './components/graphs/assets-in-maintenance/assets-in-maintenance.component';
import { AssetDowntimeTrendComponent } from './components/graphs/asset-downtime-trend/asset-downtime-trend.component';
import { TopAssetsCostComponent } from './components/graphs/top-assets-cost/top-assets-cost.component';
import { FilterService } from '../../../_shared/services/filter.service';
import { isEmptyObject } from '../../../_shared/helpers/object.helper';
import { formatDueDate } from './mockdata/next-maintenance.mock';
import { MaintenanceDashboardService } from './services/maintenance-dashboard.service';
import { Subject } from 'rxjs';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { PaginationRequest } from '../../../_shared/models/pagination-request.model';
import { NextMaintenanceResponse } from './models/maintenance-dashboard-response.model';
import { ErrorHandlerService } from '../../../_shared/services/error-handler.service';
import { getSelectedFilterLabel } from '../../../_shared/helpers/kpiWidget/selected-filter.helper';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CreateMaintenanceModalComponent } from '../../_shared/components/modals/create-maintenance-modal/create-maintenance-modal.component';
import { WorkOrderTabType } from '../../_shared/models/tabs.models';
import { EmptyStateComponent } from '../../../_shared/components/states/empty-state/empty-state.component';

@Component({
  selector: 'app-maintenance-dashboard',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    AccountFilterComponent,
    DateFilterComponent,
    OverdueMaintenanceComponent,
    UpcomingMaintenanceComponent,
    UnplannedRepairsComponent,
    MaintenanceCostYtdComponent,
    PreventiveVsReactiveComponent,
    AssetsInMaintenanceComponent,
    AssetDowntimeTrendComponent,
    TopAssetsCostComponent,
    InfiniteScrollModule,
    EmptyStateComponent
  ],
  templateUrl: './maintenance-dashboard.component.html',
  styleUrl: './maintenance-dashboard.component.scss'
})
export class MaintenanceDashboardComponent extends BasePageComponent {
  private readonly transloco = inject(TranslocoService);
  private filterService: FilterService = inject(FilterService);
  private dashboardService: MaintenanceDashboardService = inject(MaintenanceDashboardService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private ngbModal: NgbModal = inject(NgbModal);
  private cancelSubject: Subject<void> = new Subject<void>();

  isLoading: boolean = true;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  nextMaintenanceItems: NextMaintenanceResponse[] = [];
  page: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;

  constructor() {
    super();
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  onNextMaintenanceScroll(): void {
    if (this.isLoading || this.page >= this.totalPages) return;
    this.page++;
    this.loadNextMaintenance();
  }

  private refreshNextMaintenance(): void {
    this.cancelSubject.next();
    this.page = 1;
    this.totalPages = 0;
    this.nextMaintenanceItems = [];
    this.loadNextMaintenance();
  }

  private async loadNextMaintenance(): Promise<void> {
    this.isLoading = true;

    try {
      const request: PaginationRequest = {
        timeFrom: new Date(),
        timeTo: new Date(new Date().setMonth(new Date().getMonth() + 1)),
        interval: this.defaultInterval,
        accountIds: this.accountFilter.accountIds ?? [],
        page: this.page,
        pageSize: this.pageSize
      };

      const response = await this.dashboardService.getNextMaintenance(request, this.cancelSubject);
      const newItems = response.data?.results ?? [];

      this.nextMaintenanceItems = this.page === 1
        ? newItems
        : [...this.nextMaintenanceItems, ...newItems];
      this.totalPages = response.data?.totalPages ?? 0;
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  getSelectedFilterLabel(filter: DateIntervalFilterData): string {
    return getSelectedFilterLabel(filter, (key, params) => this.transloco.translate(key, params), this.transloco.getActiveLang());
  }

  onSeeMore(): void {
    this.pageFilterStateService.clearFilter();
    this.pageFilterStateService.storeFilter({
      accountIds: this.accountFilter.accountIds ?? [],
      activeTab: 'work-orders' as WorkOrderTabType
    });

    this.router.navigate(['/asset-management/work-order'], {
      queryParams: { filtered: true }
    });
  }

  onCreateMaintenanceClick(): void {
    const modalRef = this.ngbModal.open(CreateMaintenanceModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      container: 'body'
    });
    modalRef.componentInstance.accountIds = this.accountFilter.accountIds ?? [];

    modalRef.closed.subscribe(() => {
      this.refreshNextMaintenance();
    });
  }

  formatDueDate(daysUntilDue: number): string {
    return formatDueDate(daysUntilDue);
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.refreshNextMaintenance();
    this.updateFilter();
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updateFilter();
  }

  updateFilter(): void {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter({
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData);

      this.updatePageFilterState();
    }
  }

  protected override onRefresh(): void { }

  protected override shouldRefresh(): boolean { return false; }

  protected override getFilters(): any {
    return {
      ...this.dateFilter,
      ...this.accountFilter
    }
  }

  protected override applyPageFilterState(pageFilter: any): void { }
}